import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LoaderButton } from "@/components/LoaderButton";
import {
  BOTON_SUAVE,
  CAMPO,
  ENLACE,
  TEXTO,
  TEXTO_DE_ERROR,
  TEXTO_SECUNDARIO,
} from "@/components/caja/clasesDeLaEntrada";
import { mensajeDelPin } from "@/context/caja/cajeroDeLaCaja";
import { problemaDelFormato } from "@/context/caja/pinDelCajero";
import { cn } from "@/lib/utils";
import { pos } from "@/platform";
import type { PosCajeroConPin } from "@/platform/types";

interface EntrarConPinProps {
  businessId: string;
  /** Los que pueden entrar: los cajeros del mandato vigente con PIN acá. */
  cajeros: PosCajeroConPin[];
  /** El que se ofrece primero: el de la sesión, si tiene PIN. */
  preseleccionado?: string;
  /** El usuario al que core le dijo `403 not_permitted`: Rust tampoco lo deja entrar. */
  sinPermiso?: string | null;
  onEntrar: (cajero: PosCajeroConPin) => void;
  onVolver?: () => void;
}

/**
 * La entrada a la caja con el PIN del cajero (#346 AC-2; §2.6, D23 y D24):
 * sin red, o sin una sesión que pueda vender. El PIN se verifica en el equipo,
 * contra su hash, con intentos limitados.
 */
function EntrarConPin({
  businessId,
  cajeros,
  preseleccionado,
  sinPermiso,
  onEntrar,
  onVolver,
}: EntrarConPinProps) {
  const [elegido, setElegido] = useState<PosCajeroConPin | null>(
    () =>
      cajeros.find((c) => c.userId === preseleccionado) ??
      (cajeros.length === 1 ? cajeros[0] : null)
  );
  const [pin, setPin] = useState("");
  const [verificando, setVerificando] = useState(false);
  const [problema, setProblema] = useState<string | null>(null);

  const elegir = (cajero: PosCajeroConPin | null) => {
    setElegido(cajero);
    setPin("");
    setProblema(null);
  };

  const entrar = async () => {
    if (!elegido) return;
    const invalido = problemaDelFormato(pin);
    if (invalido) {
      setProblema(invalido);
      return;
    }
    setVerificando(true);
    setProblema(null);
    try {
      const verificacion = await pos.verifyPin({
        businessId,
        userId: elegido.userId,
        pin,
        sinPermiso,
      });
      const mensaje = mensajeDelPin(verificacion);
      if (mensaje === null) {
        onEntrar(elegido);
        return;
      }
      setProblema(mensaje);
      setPin("");
    } catch {
      setProblema("La caja no pudo verificar el PIN. Probá de nuevo.");
    } finally {
      setVerificando(false);
    }
  };

  return (
    <section
      aria-labelledby="entrar-con-pin"
      className={cn("flex w-full max-w-sm flex-col gap-4", TEXTO)}
    >
      <div className="flex flex-col gap-1">
        <h2 id="entrar-con-pin" className="text-xl font-semibold text-balance">
          Entrar con tu PIN
        </h2>
        <p className={cn("text-sm text-pretty", TEXTO_SECUNDARIO)}>
          Sin conexión, cada cajero entra a la caja con su PIN.
        </p>
      </div>

      {!elegido ? (
        <ul className="flex flex-col gap-2" aria-label="Cajeros">
          {cajeros.map((cajero) => (
            <li key={cajero.userId}>
              <Button
                type="button"
                variant="outline"
                className="min-h-10 w-full justify-start"
                onClick={() => elegir(cajero)}
              >
                {cajero.nombre}
              </Button>
            </li>
          ))}
        </ul>
      ) : (
        <form
          className="flex flex-col gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            void entrar();
          }}
        >
          <div className="flex flex-col gap-2">
            <Label htmlFor="pin-de-la-caja">PIN de {elegido.nombre}</Label>
            <Input
              id="pin-de-la-caja"
              type="password"
              inputMode="numeric"
              autoComplete="off"
              autoFocus
              maxLength={8}
              className={CAMPO}
              value={pin}
              onChange={(e) => setPin(e.target.value)}
            />
          </div>
          {problema && (
            <p role="alert" className={cn("text-sm", TEXTO_DE_ERROR)}>
              {problema}
            </p>
          )}
          <LoaderButton type="submit" isLoading={verificando}>
            Entrar
          </LoaderButton>
          {cajeros.length > 1 && (
            <Button
              type="button"
              variant="ghost"
              className={BOTON_SUAVE}
              disabled={verificando}
              onClick={() => elegir(null)}
            >
              Elegir otro cajero
            </Button>
          )}
        </form>
      )}

      {onVolver && (
        <Button
          type="button"
          variant="link"
          className={ENLACE}
          disabled={verificando}
          onClick={onVolver}
        >
          Volver
        </Button>
      )}
    </section>
  );
}

export default EntrarConPin;
